import { RoomVersion } from '@features/rooms/pages/Room/RoomVersion';
import { Property } from 'shared-types/Property';
import { NormalizedStore } from '@store/entity/entity.types';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Head from 'next/head';
import React, { useEffect } from 'react';
import { Router } from 'shared-routing';
import { analytics } from '@features/analytics';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { getStaticRates, ratesKeys } from '@features/rates';
import {
  getAvailableRoomById,
  getStaticRoomById,
  roomsKeys,
  useStaticRoomById,
} from '@features/rooms';
import { PagePropsWithSSR } from '@features/state';
import Error from 'next/error';
import { Routes } from '@common/constants';
import { entityQueryOutcome } from '@common/helpers/entityQueryOutcome';
import { isNotFoundError } from '@common/helpers/isNotFoundError';
import { useRedirectWhenMissing } from '@common/hooks';
import { redirectToListing } from '@common/helpers/notFoundRedirect';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { parseDateToString } from '@utils/helpers';

type RoomPageProps = {
  id: string;
  property: Property;
  language: string;
  url: string;
};

const RoomPage: NextPage<RoomPageProps> = ({ id, property, language, url }) => {
  const { data: room, isFetching, error } = useStaticRoomById(id, language);
  const { isMissing, hasFailed } = entityQueryOutcome({ data: room, isFetching, error });

  useRedirectWhenMissing(isMissing, Routes.ROOM_LIST);
  const RoomTitle = room?.title;
  const PropertyTitle = property.propertyTitle;

  const pageTitle = `${RoomTitle} ${PropertyTitle ? `| ${PropertyTitle}` : ''}`;

  useEffect(() => {
    analytics.track({
      event: 'view_item',
      params: {
        items: [{ item_category: 'rooms', item_id: room?.room_id, item_name: room?.title }],
      },
    });
  }, []);

  // The API failed, so we do not know whether this room exists. Sending the guest
  // to the listing would tell them it is gone.
  if (hasFailed) {
    return <Error statusCode={500} />;
  }

  if (isMissing) {
    return null;
  }

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            description: room ? room.description : '',
            locale: language,
            url,
            images: [
              {
                url:
                  room && room.image_url && room.image_url.length
                    ? room.image_url
                    : property.socialFallbackImage?.url || '',
              },
            ],
          },
        }}
      />
      <RoomVersion language={language} property={property} />
    </>
  );
};

type RoomInitialProps = {
  store: NormalizedStore;
} & NextPageContext &
  Pick<RoomPageProps, 'language' | 'url' | 'property'>;

RoomPage.getInitialProps = async (
  props: RoomInitialProps
): Promise<PagePropsWithSSR<RoomPageProps>> => {
  const { store, property, language, url, res, query, moduleStatuses } = props;
  const { id: siteId, slug: siteSlug } = property;

  if (!moduleStatuses.roomsEnabled) {
    if (res) {
      res.writeHead(302, {
        Location: '/',
      });
      res.end();
    } else {
      Router.push('/');
    }
  }

  const roomId = query.id as string;

  if (!res) {
    return {
      id: roomId,
      property,
      language,
      url,
    };
  }

  const state = store.getState();
  const journeyParams = yourJourneySelectors.getParsedJourneyParams(state);

  const adults = journeyParams.adults;
  const children = journeyParams.children;
  const childAges = journeyParams.childAges;
  const fromDate = parseDateToString(journeyParams.fromDate);
  const toDate = parseDateToString(journeyParams.toDate);
  const rooms = journeyParams.rooms;

  const client = new QueryClient();

  // fetchQuery, not prefetchQuery: this is the redirect decision, and prefetch
  // reports a failed request the same way it reports an unknown room.
  const staticRoomQuery = client.fetchQuery({
    queryKey: roomsKeys.staticRoomById({ siteId: property.id, id: roomId, language }),
    queryFn: () => getStaticRoomById({ siteId: property.id, id: roomId, language }),
    cacheTime: Infinity,
    staleTime: Infinity,
    // No retries: this is the redirect decision, on the critical path of a
    // response the guest is waiting for.
    retry: false,
  });

  const availableRoomQuery = client.prefetchQuery({
    queryKey: roomsKeys.availableRoomById({
      id: roomId,
      siteId,
      rooms,
      adults,
      children,
      childAges,
      fromDate,
      toDate,
      isMultiRoomsEnabled: rooms.length > 1,
    }),
    queryFn: () =>
      getAvailableRoomById({
        id: roomId,
        siteId,
        rooms,
        adults,
        children,
        childAges,
        fromDate,
        toDate,
        isMultiRoomsEnabled: rooms.length > 1,
      }),
  });

  const staticRatesQuery = client.prefetchQuery({
    queryKey: ratesKeys.staticRates({ siteSlug, language }),
    queryFn: () => getStaticRates({ siteSlug, language }),
    cacheTime: Infinity,
    staleTime: Infinity,
  });

  try {
    await Promise.all([staticRoomQuery, staticRatesQuery, availableRoomQuery]);
  } catch (error) {
    // Only an unknown room belongs on the listing. Anything else stays an error.
    if (!isNotFoundError(error)) throw error;

    redirectToListing(res, Routes.ROOM_LIST, query);
    return { id: roomId, property, language, url };
  }

  return {
    id: roomId,
    property,
    language,
    url,
    dehydratedState: dehydrate(client),
  };
};

export default RoomPage;
